import { useState, useEffect, useRef } from 'react'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import { useNavegacaoLista } from '../hooks/useNavegacaoLista'
import SenhaMasterModal from '../components/SenhaMasterModal'
import IconExcluir from '../components/icons/IconExcluir'
import IconEditar from '../components/icons/IconEditar'
import { useAuth } from '../contexts/AuthContext'
import { avisar, confirmar } from '../components/Avisos'

const tiposAnexoFinanceiro = ['Nota Fiscal', 'Recibo', 'Comprovante de Pagamento', 'Boleto', 'Contrato', 'Outros']

// DATE do Postgres vira JSON como "AAAA-MM-DDT00:00:00.000Z" (meia-noite UTC) — reconstruir com o
// "Z" faz o toLocaleDateString num fuso atras de UTC (Brasil) voltar 1 dia. Por isso sempre
// descarta a hora/fuso vindos do backend e ancora de novo em meia-noite LOCAL.
// Ano com 2 digitos (01/10/26) pra economizar espaco na tabela — pedido do usuario em 08/10.
const fmtData = (d) => d ? new Date(d.split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'
const fmtValor = (v) => v != null ? `R$ ${parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'

// Campo tipo moeda: digita livre, formata "R$ x.xxx,xx" ao sair do campo (mesmo padrao usado nos
// campos de cobertura tipo "moeda" em Apolices.jsx).
const parseMoneyInput = (v) => {
  const s = (v || '').toString()
  const negativo = s.includes('-')
  const n = parseFloat(s.replace(/[^0-9,]/g, '').replace(',', '.')) || 0
  return negativo ? -n : n
}
const formatMoneyInput = (v) => {
  const n = parseFloat(v) || 0
  const sinal = n < 0 ? '-' : ''
  return sinal + 'R$ ' + Math.abs(n).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}
// Mascara enquanto digita (09/10): os 2 ultimos digitos sao sempre os centavos — "4510" vira "R$ 45,10".
const maskMoneyInput = (v) => {
  const s = (v || '').toString()
  const negativo = s.includes('-')
  const digitos = s.replace(/\D/g, '').replace(/^0+/, '')
  if (!digitos) return negativo ? '-' : ''
  return formatMoneyInput((negativo ? -1 : 1) * parseInt(digitos, 10) / 100)
}

// Busca de categoria ignorando acentos, pontuacao e maiusculas ("comissao" acha "Comissões").
const normalizarBusca = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^0-9a-zA-Z]+/g, ' ').trim().toLowerCase()

// Mesma mascara de CPF/CNPJ de Clientes.jsx (ate 11 digitos = CPF, acima = CNPJ)
const maskCPFCNPJ = v => {
  v = v.replace(/\D/g, '')
  if (v.length <= 11) {
    v = v.replace(/(\d{3})(\d)/, '$1.$2')
    v = v.replace(/(\d{3})(\d)/, '$1.$2')
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  } else {
    v = v.replace(/^(\d{2})(\d)/, '$1.$2')
    v = v.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    v = v.replace(/\.(\d{3})(\d)/, '.$1/$2')
    v = v.replace(/(\d{4})(\d)/, '$1-$2')
  }
  return v.slice(0, 18)
}

const statusInfo = (s) => s === 'pago'
  ? { label: 'Pago', cls: 'badge-green' }
  : { label: 'Pendente', cls: 'badge-amber' }

const initForm = {
  tipo: 'entrada', origem_destino_id: '', categoria_financeira_id: '',
  valor: '', data_vencimento: '', valor_pago: '', data_pagamento: '',
  observacoes: '', socio_desconto_id: '', socio_desconto_descricao: '',
  repeticao: 'unica', num_parcelas: '2', valor_modo: 'parcela',
}

// Etiqueta mostrada ao lado do nome na lista e no modal: "3/10" (parcelada) ou "Mensal" (recorrente)
const etiquetaRecorrencia = (l) => {
  if (!l.recorrencia_modo) return null
  return l.recorrencia_modo === 'parcelada' ? `${l.parcela_numero}/${l.recorrencia_total}` : '↻ Mensal'
}

const colunasFinanceiro = [
  { key: 'origem_destino',    label: 'Origem/Destino',      get: l => l.origem_destino_nome || l.descricao || '' },
  { key: 'data_lancamento',   label: 'Data de\nLançamento', get: l => l.data_lancamento || '', date: true },
  { key: 'categoria',         label: 'Categoria',           get: l => l.categoria_nome || '' },
  { key: 'vencimento',        label: 'Vencimento',          get: l => l.data_vencimento || '', date: true },
  { key: 'valor_vencimento',  label: 'Valor do\nVencimento', get: l => parseFloat(l.valor) || 0, numeric: true },
  { key: 'pagamento',         label: 'Pagamento',           get: l => l.data_pagamento || '', date: true },
  { key: 'valor_pagamento',   label: 'Valor do\nPagamento', get: l => l.data_pagamento && l.valor_pago != null ? parseFloat(l.valor_pago) : 0, numeric: true },
  { key: 'observacoes',       label: 'Observações',         get: l => l.observacoes || '' },
  { key: 'status',            label: 'Status',             get: l => statusInfo(l.status).label },
]

// ===== Busca de Origem/Destino com autocomplete + "+" pra cadastrar rapido, filtrada pelo Tipo
// selecionado no lançamento (mesmo padrão do OficinaPicker de Sinistros.jsx) =====
function OrigemDestinoPicker({ tipo, origens, origemDestinoId, onSelecionar, onCriado, inputRef }) {
  const [busca, setBusca] = useState('')
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false)
  const [modalNovo, setModalNovo] = useState(false)

  useEffect(() => {
    const atual = origens.find(o => String(o.id) === String(origemDestinoId))
    setBusca(atual ? atual.nome : '')
  }, [origemDestinoId, origens])

  const sugestoes = busca.trim()
    ? origens.filter(o => o.tipo === tipo && o.nome.toLowerCase().includes(busca.trim().toLowerCase())).slice(0, 8)
    : []
  const navBusca = useNavegacaoLista(sugestoes.length, (i) => {
    const o = sugestoes[i]
    onSelecionar(o.id); setBusca(o.nome); setMostrarSugestoes(false)
  })

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input ref={inputRef} className="form-input" value={busca}
          onChange={e => { setBusca(e.target.value); setMostrarSugestoes(true); if (!e.target.value) onSelecionar(''); navBusca.setIndice(0) }}
          onFocus={() => setMostrarSugestoes(true)}
          onBlur={() => setTimeout(() => setMostrarSugestoes(false), 150)}
          onKeyDown={navBusca.onKeyDown}
          placeholder="Buscar por nome..." autoComplete="off" />
        <button type="button" className="btn btn-secondary" onClick={() => setModalNovo(true)}>+</button>
      </div>
      {mostrarSugestoes && sugestoes.length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
          {sugestoes.map((o, i) => (
            <button key={o.id} type="button" onMouseDown={() => { onSelecionar(o.id); setBusca(o.nome); setMostrarSugestoes(false) }}
              onMouseEnter={() => navBusca.setIndice(i)}
              style={{ width: '100%', textAlign: 'left', border: 'none', background: i === navBusca.indice ? 'rgba(var(--primary-rgb),0.08)' : '#fff', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5' }}>
              <div style={{ fontSize: 13, color: '#1A1A2E', fontWeight: 600 }}>{o.nome}</div>
              {o.cpf_cnpj && <div style={{ fontSize: 11, color: '#7A7F87', marginTop: 2 }}>{o.cpf_cnpj}</div>}
            </button>
          ))}
        </div>
      )}
      {modalNovo && (
        <NovoOrigemDestinoModal
          tipo={tipo} nomeInicial={busca}
          onClose={() => setModalNovo(false)}
          onCriado={(novo) => { onCriado(novo); onSelecionar(novo.id); setBusca(novo.nome); setModalNovo(false) }}
        />
      )}
    </div>
  )
}

function NovoOrigemDestinoModal({ tipo, nomeInicial, onClose, onCriado }) {
  const [form, setForm] = useState({ nome: nomeInicial || '', cpf_cnpj: '' })
  const [salvando, setSalvando] = useState(false)

  const salvar = async () => {
    if (!form.nome.trim()) { avisar('Informe o nome.'); return }
    setSalvando(true)
    try {
      const { data } = await api.post('/cadastro/origem-destino', { tipo, ...form })
      onCriado(data)
    } catch (err) {
      avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 380 }}>
        <div className="modal-header">
          <span className="modal-title">Novo {tipo === 'entrada' ? 'Origem' : 'Destino'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Nome *</label>
          <input className="form-input" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">CPF/CNPJ (opcional)</label>
          <input className="form-input" value={form.cpf_cnpj} onChange={e => setForm(f => ({ ...f, cpf_cnpj: maskCPFCNPJ(e.target.value) }))} placeholder="000.000.000-00 ou 00.000.000/0000-00" />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}

// ===== Busca de Categoria com autocomplete (sem "+" — categorias só se cadastram em Cadastro >
// Financeiro) — mesmo padrão do OrigemDestinoPicker, útil porque a árvore pode ter muitas folhas =====
function CategoriaFinanceiraPicker({ categoriasFolha, categoriaId, onSelecionar }) {
  const [busca, setBusca] = useState('')
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false)

  useEffect(() => {
    const atual = categoriasFolha.find(c => String(c.id) === String(categoriaId))
    setBusca(atual ? atual.caminho : '')
  }, [categoriaId, categoriasFolha])

  const sugestoes = busca.trim()
    ? categoriasFolha.filter(c => normalizarBusca(c.caminho).includes(normalizarBusca(busca))).slice(0, 10)
    : categoriasFolha.slice(0, 10)
  const navBusca = useNavegacaoLista(sugestoes.length, (i) => {
    const c = sugestoes[i]
    onSelecionar(c.id); setBusca(c.caminho); setMostrarSugestoes(false)
  })

  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" value={busca}
        onChange={e => { setBusca(e.target.value); setMostrarSugestoes(true); if (!e.target.value) onSelecionar(''); navBusca.reset() }}
        onFocus={() => setMostrarSugestoes(true)}
        onBlur={() => setTimeout(() => setMostrarSugestoes(false), 150)}
        onKeyDown={navBusca.onKeyDown}
        placeholder={categoriasFolha.length === 0 ? 'Nenhuma cadastrada' : 'Buscar categoria...'}
        disabled={categoriasFolha.length === 0}
        autoComplete="off" />
      {mostrarSugestoes && sugestoes.length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
          {sugestoes.map((c, i) => (
            <button key={c.id} type="button" onMouseDown={() => { onSelecionar(c.id); setBusca(c.caminho); setMostrarSugestoes(false) }}
              onMouseEnter={() => navBusca.setIndice(i)}
              style={{ width: '100%', textAlign: 'left', border: 'none', background: i === navBusca.indice ? 'rgba(var(--primary-rgb),0.08)' : '#fff', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5', fontSize: 13, color: '#1A1A2E' }}>
              {c.caminho}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ===== Anexos (mesmo padrão de sinistro_anexos/assistencia_anexos) =====
function AnexosFinanceiro({ id }) {
  const [anexos, setAnexos] = useState([])
  const [novoAnexo, setNovoAnexo] = useState(null)
  const [enviando, setEnviando] = useState(false)

  const carregar = () => {
    api.get(`/financeiro/${id}/anexos`)
      .then(r => setAnexos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAnexos([]))
  }
  useEffect(() => { carregar() }, [id])

  const selecionarArquivo = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { avisar('Arquivo muito grande. Máximo 5MB.'); return }
    setNovoAnexo(n => ({ ...n, arquivo: file }))
  }

  const confirmarAnexo = async () => {
    if (!novoAnexo?.arquivo) { avisar('Selecione um arquivo.'); return }
    if (!novoAnexo?.tipo) { avisar('Selecione o tipo do documento.'); return }
    setEnviando(true)
    try {
      const dados = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = ev => resolve(ev.target.result)
        reader.onerror = reject
        reader.readAsDataURL(novoAnexo.arquivo)
      })
      await api.post(`/financeiro/${id}/anexos`, {
        tipo: novoAnexo.tipo, nome: novoAnexo.arquivo.name, tamanho: novoAnexo.arquivo.size, dados
      })
      carregar()
      setNovoAnexo(null)
    } catch (err) {
      avisar('Erro ao enviar anexo: ' + (err.response?.data?.erro || err.message))
    } finally {
      setEnviando(false)
    }
  }

  const remover = async (anexoId) => {
    if (!(await confirmar('Remover este anexo?'))) return
    await api.delete(`/financeiro/anexos/${anexoId}`)
    carregar()
  }

  const visualizar = async (a) => {
    try {
      const { data } = await api.get(`/financeiro/anexos/${a.id}/dados`)
      const parts = data.dados.split(',')
      const mimeType = parts[0].split(':')[1].split(';')[0]
      const byteStr = atob(parts[1])
      const ab = new ArrayBuffer(byteStr.length)
      const ia = new Uint8Array(ab)
      for (let x = 0; x < byteStr.length; x++) ia[x] = byteStr.charCodeAt(x)
      const blob = new Blob([ab], { type: mimeType })
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (err) {
      avisar('Erro ao abrir anexo: ' + (err.response?.data?.erro || err.message))
    }
  }

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }
  const getIcon = (nome) => {
    const ext = (nome || '').split('.').pop().toLowerCase()
    if (ext === 'pdf') return '📕'
    if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️'
    return '📄'
  }

  return (
    <>
      {anexos.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
          <tbody>
            {anexos.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, background: 'var(--primary-light)', color: 'var(--primary)', padding: '3px 8px', borderRadius: 6, fontWeight: 500, flexShrink: 0 }}>{a.tipo}</span>
                    <span style={{ fontSize: 18 }}>{getIcon(a.nome)}</span>
                    <button type="button" onClick={() => visualizar(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'var(--primary)', textDecoration: 'underline', fontFamily: 'inherit', wordBreak: 'break-all', textAlign: 'left' }}>{a.nome}</button>
                  </div>
                </td>
                <td style={{ padding: '8px 10px', fontSize: 12, color: '#888' }}>{formatSize(a.tamanho || 0)}</td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => visualizar(a)}>Visualizar</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => remover(a.id)}>Remover</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {anexos.length === 0 && !novoAnexo && (
        <div style={{ padding: 24, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, marginBottom: 16 }}>
          Nenhum anexo adicionado ainda.
        </div>
      )}
      {novoAnexo && (
        <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 12 }}>Novo Anexo</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 12, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Arquivo</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={selecionarArquivo} style={{ fontSize: 13, padding: '6px 0', display: 'block', width: '100%' }} />
              {novoAnexo.arquivo && <span style={{ fontSize: 11, color: '#3B6D11', marginTop: 4, display: 'block' }}>✓ {novoAnexo.arquivo.name} ({formatSize(novoAnexo.arquivo.size)})</span>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Tipo do Documento</label>
              <select className="form-select" value={novoAnexo.tipo} onChange={e => setNovoAnexo(n => ({ ...n, tipo: e.target.value }))}>
                <option value="">Selecione...</option>
                {tiposAnexoFinanceiro.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={confirmarAnexo} disabled={enviando}>{enviando ? 'Enviando...' : '✓ Salvar anexo'}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNovoAnexo(null)}>Cancelar</button>
          </div>
        </div>
      )}
      {!novoAnexo && <button type="button" className="btn btn-secondary" onClick={() => setNovoAnexo({ arquivo: null, tipo: '' })}>+ Adicionar novo anexo</button>}
      <div style={{ marginTop: 12, fontSize: 11, color: '#aaa' }}>Formatos aceitos: PDF, JPG, PNG. Tamanho máximo: 5MB por arquivo.</div>
    </>
  )
}

// ===== Modal de Novo Lançamento / edição =====
function LancamentoModal({ lancamentoId: lancamentoIdInicial, onClose, onSaved }) {
  const [lancamentoId, setLancamentoId] = useState(lancamentoIdInicial || null)
  const [form, setForm] = useState(initForm)
  const [origens, setOrigens] = useState([])
  // "Descontar de" socio (Recibo de Socios, 09/10): so admin ve; a compra lancada aqui aparece
  // sozinha no recibo do socio, sem precisar cadastrar de novo la.
  const { usuario } = useAuth()
  const ehAdmin = usuario?.perfil === 'admin'
  const [socios, setSocios] = useState([])
  useEffect(() => {
    if (ehAdmin) api.get('/socios').then(r => setSocios(Array.isArray(r.data) ? r.data : [])).catch(() => setSocios([]))
  }, [ehAdmin])
  const [categorias, setCategorias] = useState([])
  const [salvando, setSalvando] = useState(false)
  const [carregando, setCarregando] = useState(!!lancamentoIdInicial)
  const [pedirSenhaMasterExcluir, setPedirSenhaMasterExcluir] = useState(false)
  const [confirmarExcluir, setConfirmarExcluir] = useState(false)
  const [adicionado, setAdicionado] = useState(false)
  const origemInputRef = useRef(null)
  const categoriaSugeridaRef = useRef('')

  // Novo lancamento: ao escolher a Origem/Destino, traz a categoria do ultimo lancamento dela.
  // So substitui a categoria se ela estiver vazia ou se foi a propria sugestao anterior (nao
  // atropela uma categoria escolhida a mao). Continua podendo trocar.
  const selecionarOrigem = (id) => {
    setField('origem_destino_id', id)
    if (lancamentoId || !id) return
    api.get(`/financeiro/ultima-categoria?origem_destino_id=${id}`).then(r => {
      const cat = r.data?.categoria_financeira_id
      if (!cat) return
      setForm(f => {
        if (String(f.origem_destino_id) !== String(id)) return f
        if (f.categoria_financeira_id && String(f.categoria_financeira_id) !== String(categoriaSugeridaRef.current)) return f
        categoriaSugeridaRef.current = cat
        return { ...f, categoria_financeira_id: cat }
      })
    }).catch(() => {})
  }
  const [recorrencia, setRecorrencia] = useState(null)
  const [confirmarEncerrar, setConfirmarEncerrar] = useState(false)
  // Lancamento parcelado/recorrente: ao mudar valor, origem/destino ou categoria, pergunta se vale
  // so pra este ou tambem pros seguintes ainda nao pagos (pedido do usuario em 09/10).
  const [perguntarEscopo, setPerguntarEscopo] = useState(false)
  const originalRef = useRef(null)
  const [pedirSenhaMasterEncerrar, setPedirSenhaMasterEncerrar] = useState(false)

  useEffect(() => {
    api.get('/cadastro/origem-destino').then(r => setOrigens(Array.isArray(r.data) ? r.data : [])).catch(() => setOrigens([]))
  }, [])

  useEffect(() => {
    api.get(`/cadastro/categorias-financeiras?tipo=${form.tipo}`).then(r => setCategorias(Array.isArray(r.data) ? r.data : [])).catch(() => setCategorias([]))
  }, [form.tipo])

  useEffect(() => {
    if (!lancamentoIdInicial) return
    setCarregando(true)
    api.get(`/financeiro/${lancamentoIdInicial}`).then(r => {
      const l = r.data
      const valorFmt = l.valor != null ? formatMoneyInput(l.valor) : ''
      setForm({
        tipo: l.tipo,
        origem_destino_id: l.origem_destino_id || '',
        categoria_financeira_id: l.categoria_financeira_id || '',
        valor: valorFmt,
        valor_anterior: valorFmt,
        data_vencimento: l.data_vencimento?.split('T')[0] || '',
        // Pendente sem valor de pagamento (ex.: parcelas/recorrencias geradas) ja abre com o valor
        // do vencimento sugerido, pra baixa so precisar da data.
        valor_pago: l.valor_pago != null ? formatMoneyInput(l.valor_pago) : valorFmt,
        data_pagamento: l.data_pagamento?.split('T')[0] || '',
        observacoes: l.observacoes || '',
        socio_desconto_id: l.socio_desconto_id ? String(l.socio_desconto_id) : '',
        socio_desconto_descricao: l.socio_desconto_descricao || '',
        repeticao: 'unica', num_parcelas: '2', valor_modo: 'parcela',
      })
      setRecorrencia(l.recorrencia_modo ? l : null)
      originalRef.current = { valor: parseMoneyInput(valorFmt), origem_destino_id: String(l.origem_destino_id || ''), categoria_financeira_id: String(l.categoria_financeira_id || ''), socio: `${l.socio_desconto_id || ''}|${l.socio_desconto_descricao || ''}` }
    }).catch(() => {}).finally(() => setCarregando(false))
  }, [lancamentoIdInicial])

  const setField = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }))

  // Só o 3º nível (folha) da árvore de categorias aparece pra escolher — nível 1/2 são só
  // organização pro relatório. Label mostra o caminho completo pra dar contexto.
  const porId = Object.fromEntries(categorias.map(c => [c.id, c]))
  const categoriasFolha = categorias.filter(c => c.nivel === 3).map(c3 => {
    const c2 = porId[c3.categoria_pai_id]
    const c1 = c2 ? porId[c2.categoria_pai_id] : null
    return { id: c3.id, caminho: [c1?.nome, c2?.nome, c3.nome].filter(Boolean).join(' › ') }
  })

  const status = form.data_pagamento ? 'pago' : 'pendente'

  // Editar um lancamento ja existente fecha ao salvar (igual antes). Um lancamento novo, ao
  // "Adicionar", fica na tela — reseta o formulario (mantendo o Tipo escolhido) pra poder
  // cadastrar varios seguidos; so fecha quando o usuario clica em "Fechar".
  const salvar = async () => {
    if (!form.origem_destino_id) { avisar('Selecione a Origem/Destino.'); return }
    if (!form.valor) { avisar('Informe o Valor.'); return }
    if (!form.data_vencimento) { avisar('Informe a Data de Vencimento.'); return }
    if (!lancamentoId && form.repeticao === 'parcelada') {
      const n = parseInt(form.num_parcelas, 10)
      if (!(n >= 2 && n <= 360)) { avisar('Informe o número de parcelas (entre 2 e 360).'); return }
    }
    if (lancamentoId && recorrencia) {
      const o = originalRef.current || {}
      const mudouComum = parseMoneyInput(form.valor) !== o.valor
        || String(form.origem_destino_id || '') !== o.origem_destino_id
        || String(form.categoria_financeira_id || '') !== o.categoria_financeira_id
        || (ehAdmin && `${form.socio_desconto_id || ''}|${form.socio_desconto_descricao || ''}` !== o.socio)
      if (mudouComum) { setPerguntarEscopo(true); return }
    }
    gravar(false)
  }

  const gravar = async (aplicarPosteriores) => {
    setPerguntarEscopo(false)
    setSalvando(true)
    try {
      if (lancamentoId) {
        const { data } = await api.put(`/financeiro/${lancamentoId}`, { ...form, aplicar_posteriores: aplicarPosteriores })
        if (aplicarPosteriores) avisar(`Alterado este lançamento e mais ${data?.posteriores_alterados || 0} seguinte(s) pendente(s).`)
        onSaved?.()
        onClose()
      } else {
        const { data } = await api.post('/financeiro', form)
        onSaved?.()
        setForm(f => ({ ...initForm, tipo: f.tipo }))
        setAdicionado(data?.qtd_gerada > 1 ? `✓ ${data.qtd_gerada} lançamentos adicionados` : '✓ Lançamento adicionado')
        setTimeout(() => setAdicionado(false), 3000)
        // Cursor ja no campo Origem/Destino pro proximo lancamento (o Tipo fica o mesmo).
        setTimeout(() => origemInputRef.current?.focus(), 0)
      }
    } catch (err) {
      avisar('Erro ao salvar lançamento: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  const excluir = () => setConfirmarExcluir(true)
  const confirmarExclusaoSimples = () => { setConfirmarExcluir(false); setPedirSenhaMasterExcluir(true) }
  const confirmarExclusaoComSenha = async (senha) => {
    await api.delete(`/financeiro/${lancamentoId}`, { data: { senha_master: senha } })
    setPedirSenhaMasterExcluir(false)
    onSaved?.()
    onClose()
  }

  const confirmarEncerramentoComSenha = async (senha) => {
    const { data } = await api.post(`/financeiro/${lancamentoId}/encerrar-recorrencia`, { senha_master: senha })
    setPedirSenhaMasterEncerrar(false)
    setRecorrencia(r => r && { ...r, recorrencia_ativa: false })
    onSaved?.()
    avisar(`${data.removidos} lançamento(s) futuro(s) não pago(s) excluído(s).`)
  }

  const valorNum = parseMoneyInput(form.valor)
  const nParcelas = parseInt(form.num_parcelas, 10) || 0
  const resumoParcelas = form.repeticao === 'parcelada' && valorNum && nParcelas >= 2
    ? (form.valor_modo === 'total'
      ? `${nParcelas}x de ${formatMoneyInput(valorNum / nParcelas)} (total ${formatMoneyInput(valorNum)})`
      : `${nParcelas}x de ${formatMoneyInput(valorNum)} (total ${formatMoneyInput(valorNum * nParcelas)})`)
    : null

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <span className="modal-title">{lancamentoId ? 'Editar Lançamento' : 'Novo Lançamento'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {carregando ? <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Carregando...</div> : (
        <>
          <div className="form-group">
            <label className="form-label">Tipo *</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {['entrada', 'saida'].map(t => (
                <button key={t} type="button"
                  className={`btn ${form.tipo === t ? (t === 'entrada' ? 'btn-success' : 'btn-danger') : 'btn-secondary'}`}
                  style={{ flex: 1 }}
                  onClick={() => setForm(f => ({ ...f, tipo: t, origem_destino_id: '', categoria_financeira_id: '' }))}>
                  {t === 'entrada' ? '↑ Entrada' : '↓ Saída'}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{form.tipo === 'entrada' ? 'Origem *' : 'Destino *'}</label>
            <OrigemDestinoPicker inputRef={origemInputRef} tipo={form.tipo} origens={origens} origemDestinoId={form.origem_destino_id}
              onSelecionar={selecionarOrigem}
              onCriado={novo => setOrigens(prev => [...prev, novo])} />
          </div>

          <div className="form-group">
            <label className="form-label">Categoria</label>
            <CategoriaFinanceiraPicker categoriasFolha={categoriasFolha} categoriaId={form.categoria_financeira_id} onSelecionar={id => setField('categoria_financeira_id', id)} />
            {categoriasFolha.length === 0 && (
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Cadastre categorias em Cadastro &gt; Financeiro.</div>
            )}
          </div>

          {!lancamentoId && (
            <div className="form-group">
              <label className="form-label">Repetição</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[['unica', 'Única'], ['parcelada', 'Parcelada'], ['recorrente', '↻ Recorrente (todo mês)']].map(([v, rot]) => (
                  <button key={v} type="button" className={`btn ${form.repeticao === v ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }}
                    onClick={() => setField('repeticao', v)}>{rot}</button>
                ))}
              </div>
              {form.repeticao === 'parcelada' && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginTop: 10 }}>
                  <div style={{ width: 140 }}>
                    <label className="form-label">Nº de parcelas *</label>
                    <input className="form-input" type="number" min={2} max={360} value={form.num_parcelas} onChange={e => setField('num_parcelas', e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">O valor informado é</label>
                    <select className="form-select" value={form.valor_modo} onChange={e => setField('valor_modo', e.target.value)}>
                      <option value="parcela">Valor de cada parcela</option>
                      <option value="total">Valor total (dividir entre as parcelas)</option>
                    </select>
                  </div>
                </div>
              )}
              {resumoParcelas && <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>{resumoParcelas} — uma por mês a partir do 1º vencimento.</div>}
              {form.repeticao === 'recorrente' && (
                <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>Repete todo mês no mesmo dia do vencimento, sem data de fim (fica sempre gerado até 12 meses à frente). Para parar, abra um lançamento dela e clique em "Encerrar recorrência".</div>
              )}
            </div>
          )}

          {lancamentoId && recorrencia && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, background: '#f8f9fa', border: '1px solid #eee', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
              <span>
                {recorrencia.recorrencia_modo === 'parcelada'
                  ? <>Parcela <b>{recorrencia.parcela_numero}/{recorrencia.recorrencia_total}</b></>
                  : <>Recorrência mensal — ocorrência <b>{recorrencia.parcela_numero}</b></>}
                {!recorrencia.recorrencia_ativa && <span style={{ color: '#A32D2D', marginLeft: 8 }}>(encerrada)</span>}
              </span>
              {recorrencia.recorrencia_ativa && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmarEncerrar(true)}>
                  {recorrencia.recorrencia_modo === 'parcelada' ? 'Cancelar parcelas seguintes' : 'Encerrar recorrência'}
                </button>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 0 10px' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</span>
            <span className={`badge ${statusInfo(status).cls}`}>{statusInfo(status).label}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ background: '#f8f9fa', border: '1px solid #eee', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Vencimento</div>
              <div className="form-group">
                <label className="form-label">{!lancamentoId && form.repeticao !== 'unica' ? 'Vencimento (1ª parcela) *' : 'Data de Vencimento *'}</label>
                <input className="form-input" type="date" value={form.data_vencimento} onChange={e => setField('data_vencimento', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Valor (Vencimento) *</label>
                <input className="form-input" value={form.valor} onChange={e => setField('valor', maskMoneyInput(e.target.value))}
                  onBlur={e => {
                    const novo = e.target.value.trim() === '' ? '' : formatMoneyInput(parseMoneyInput(e.target.value))
                    // Valor (Pagamento) acompanha o do Vencimento enquanto nao foi alterado a mao
                    // (vazio ou igual ao valor anterior) — continua editavel pra dar baixa com outro valor.
                    setForm(f => ({
                      ...f, valor: novo,
                      valor_pago: (!f.valor_pago || parseMoneyInput(f.valor_pago) === parseMoneyInput(f.valor_anterior ?? '')) ? novo : f.valor_pago,
                      valor_anterior: novo,
                    }))
                  }}
                  placeholder="R$ 0,00" />
              </div>
            </div>
            <div style={{ background: '#f8f9fa', border: '1px solid #eee', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Pagamento</div>
              <div className="form-group">
                <label className="form-label">Data de Pagamento</label>
                <input className="form-input" type="date" value={form.data_pagamento} onChange={e => setField('data_pagamento', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Valor (Pagamento)</label>
                <input className="form-input" value={form.valor_pago} onChange={e => setField('valor_pago', maskMoneyInput(e.target.value))}
                  onBlur={e => setField('valor_pago', e.target.value.trim() === '' ? '' : formatMoneyInput(parseMoneyInput(e.target.value)))}
                  placeholder="R$ 0,00" />
              </div>
            </div>
          </div>

          {ehAdmin && form.tipo === 'saida' && socios.length > 0 && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Descontar de (Recibo de Sócios)</label>
                <select className="form-input" value={form.socio_desconto_id} onChange={e => setField('socio_desconto_id', e.target.value)}>
                  <option value="">Ninguém</option>
                  {socios.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
              {form.socio_desconto_id && (
                <div className="form-group" style={{ flex: 1.4 }}>
                  <label className="form-label">Descrição no recibo</label>
                  <input className="form-input" value={form.socio_desconto_descricao} onChange={e => setField('socio_desconto_descricao', e.target.value)}
                    placeholder="Ex.: Ótica Otto (vazio = nome da origem/destino)" />
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Observações</label>
            <textarea className="form-input" rows={3} style={{ resize: 'vertical' }} value={form.observacoes} onChange={e => setField('observacoes', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Anexos</label>
            {lancamentoId
              ? <AnexosFinanceiro id={lancamentoId} />
              : <div style={{ padding: 24, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>Salve o lançamento primeiro.</div>}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', marginTop: 8 }}>
            {adicionado && <span style={{ fontSize: 12, color: '#3B6D11' }}>{adicionado}</span>}
            {lancamentoId && <button type="button" className="btn btn-danger btn-icon" title="Excluir" onClick={excluir}><IconExcluir size={16} /></button>}
            <button type="button" className="btn btn-secondary" onClick={onClose}>Fechar</button>
            <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>
              {salvando ? (lancamentoId ? 'Salvando...' : 'Adicionando...') : (lancamentoId ? 'Salvar' : 'Adicionar')}
            </button>
          </div>
        </>
        )}
      </div>
      {confirmarExcluir && (
        <div className="modal-overlay" style={{ zIndex: 300 }} onClick={() => setConfirmarExcluir(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>Excluir lançamento?</h2>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 20px' }}>Essa ação não pode ser desfeita.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmarExcluir(false)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={confirmarExclusaoSimples}>Excluir</button>
            </div>
          </div>
        </div>
      )}
      {perguntarEscopo && (
        <div className="modal-overlay" style={{ zIndex: 300 }} onClick={() => setPerguntarEscopo(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>Aplicar a alteração a quais lançamentos?</h2>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 20px' }}>
              Este lançamento faz parte de {recorrencia?.recorrencia_modo === 'parcelada' ? 'um parcelamento' : 'uma recorrência'}.
              Em "Este e os seguintes", o valor, a origem/destino, a categoria e o "Descontar de" também mudam nos lançamentos
              posteriores ainda pendentes. Os já pagos não são alterados.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setPerguntarEscopo(false)}>Voltar</button>
              <button type="button" className="btn btn-secondary" onClick={() => gravar(false)}>Somente este</button>
              <button type="button" className="btn btn-primary" onClick={() => gravar(true)}>Este e os seguintes</button>
            </div>
          </div>
        </div>
      )}
      {confirmarEncerrar && (
        <div className="modal-overlay" style={{ zIndex: 300 }} onClick={() => setConfirmarEncerrar(false)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>
              {recorrencia?.recorrencia_modo === 'parcelada' ? 'Cancelar parcelas seguintes?' : 'Encerrar recorrência?'}
            </h2>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 20px' }}>
              Os lançamentos seguintes a este que ainda não foram pagos serão excluídos e não serão gerados novos. Este lançamento e os já pagos continuam.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmarEncerrar(false)}>Voltar</button>
              <button type="button" className="btn btn-danger" onClick={() => { setConfirmarEncerrar(false); setPedirSenhaMasterEncerrar(true) }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
      {pedirSenhaMasterEncerrar && (
        <SenhaMasterModal
          titulo={recorrencia?.recorrencia_modo === 'parcelada' ? 'Cancelar parcelas seguintes?' : 'Encerrar recorrência?'}
          mensagem="Confirme com a Senha Master para excluir os lançamentos futuros não pagos."
          onCancelar={() => setPedirSenhaMasterEncerrar(false)}
          onConfirmar={confirmarEncerramentoComSenha}
        />
      )}
      {pedirSenhaMasterExcluir && (
        <SenhaMasterModal
          titulo="Excluir lançamento?"
          mensagem="Confirme com a Senha Master para excluir este lançamento definitivamente."
          onCancelar={() => setPedirSenhaMasterExcluir(false)}
          onConfirmar={confirmarExclusaoComSenha}
        />
      )}
    </div>
  )
}

const CHAVE_FILTROS = 'fluxoCaixa.filtros'
const CAMPO_PELA_DATA = { lancamento: 'data_lancamento', vencimento: 'data_vencimento', quitacao: 'data_pagamento' }
const lerFiltrosSalvos = () => {
  try { return JSON.parse(localStorage.getItem(CHAVE_FILTROS)) || {} } catch { return {} }
}

export default function FluxoCaixa() {
  const [lancamentos, setLancamentos] = useState([])
  const [modal, setModal] = useState(false)
  const [lancamentoEditId, setLancamentoEditId] = useState(null)
  // Filtros ficam salvos neste navegador ate o "Limpar filtros" (pedido do usuario em 08/10).
  const filtrosSalvos = lerFiltrosSalvos()
  const [filtro, setFiltro] = useState(filtrosSalvos.filtro || 'todos')
  const [filtroStatus, setFiltroStatus] = useState(filtrosSalvos.filtroStatus || '')
  const [filtroCategoriaId, setFiltroCategoriaId] = useState(filtrosSalvos.filtroCategoriaId || '')
  const [filtroOrigemDestinoId, setFiltroOrigemDestinoId] = useState(filtrosSalvos.filtroOrigemDestinoId || '')
  // Periodo de analise: escolhe por qual data filtrar ('' = Todos, sem filtro de data) + Inicio/Fim.
  const [filtroPelaData, setFiltroPelaData] = useState(filtrosSalvos.filtroPelaData || '')
  const [filtroDataInicio, setFiltroDataInicio] = useState(filtrosSalvos.filtroDataInicio || '')
  const [filtroDataFim, setFiltroDataFim] = useState(filtrosSalvos.filtroDataFim || '')
  const [categoriasFiltro, setCategoriasFiltro] = useState([])
  const [origensFiltro, setOrigensFiltro] = useState([])
  // Padrao: Vencimento, do mais antigo pro mais novo.
  const [sortCol, setSortCol] = useState('vencimento')
  const [sortDir, setSortDir] = useState('asc')

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_FILTROS, JSON.stringify({
        filtro, filtroStatus, filtroCategoriaId, filtroOrigemDestinoId,
        filtroPelaData, filtroDataInicio, filtroDataFim,
      }))
    } catch { /* navegador sem localStorage: so nao persiste */ }
  }, [filtro, filtroStatus, filtroCategoriaId, filtroOrigemDestinoId, filtroPelaData, filtroDataInicio, filtroDataFim])
  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const carregar = () => {
    api.get('/financeiro').then(r => setLancamentos(r.data))
  }
  useEffect(() => {
    carregar()
    // Listas completas do cadastro (nao so o que aparece nos ultimos lancamentos), pros filtros
    // de Categoria e Origem/Destino.
    api.get('/cadastro/categorias-financeiras').then(r => {
      const todas = Array.isArray(r.data) ? r.data : []
      const porId = Object.fromEntries(todas.map(c => [c.id, c]))
      const folhas = todas.filter(c => c.nivel === 3).map(c3 => {
        const c2 = porId[c3.categoria_pai_id]
        const c1 = c2 ? porId[c2.categoria_pai_id] : null
        return { id: c3.id, caminho: [c1?.nome, c2?.nome, c3.nome].filter(Boolean).join(' › ') }
      })
      setCategoriasFiltro(folhas)
    }).catch(() => setCategoriasFiltro([]))
    api.get('/cadastro/origem-destino').then(r => setOrigensFiltro(Array.isArray(r.data) ? r.data : [])).catch(() => setOrigensFiltro([]))
  }, [])

  const limparFiltros = () => {
    setFiltro('todos'); setFiltroStatus(''); setFiltroCategoriaId(''); setFiltroOrigemDestinoId('')
    setFiltroPelaData(''); setFiltroDataInicio(''); setFiltroDataFim('')
  }

  const abrirNovo = () => { setLancamentoEditId(null); setModal(true) }
  const abrirEditar = (l) => { setLancamentoEditId(l.id); setModal(true) }
  const fecharModal = () => { setModal(false); setLancamentoEditId(null); carregar() }

  const filtrados = lancamentos.filter(l => {
    if (filtro !== 'todos' && l.tipo !== filtro) return false
    if (filtroStatus && l.status !== filtroStatus) return false
    if (filtroCategoriaId && String(l.categoria_financeira_id) !== String(filtroCategoriaId)) return false
    if (filtroOrigemDestinoId && String(l.origem_destino_id) !== String(filtroOrigemDestinoId)) return false
    if (filtroPelaData && (filtroDataInicio || filtroDataFim)) {
      const d = l[CAMPO_PELA_DATA[filtroPelaData]]?.split('T')[0]
      if (filtroDataInicio && (!d || d < filtroDataInicio)) return false
      if (filtroDataFim && (!d || d > filtroDataFim)) return false
    }
    return true
  })
  const filtradosOrdenados = sortRows(filtrados, colunasFinanceiro, sortCol, sortDir)
  const paginacao = usePaginacao(filtradosOrdenados)

  // Cards do topo: sem nenhum filtro aplicado, mostram o mes atual (por Data de Vencimento);
  // assim que algum filtro e usado, passam a refletir exatamente o que foi filtrado na tabela.
  const algumFiltroAtivo = filtro !== 'todos' || !!filtroStatus || !!filtroCategoriaId || !!filtroOrigemDestinoId
    || (!!filtroPelaData && (!!filtroDataInicio || !!filtroDataFim))
  const hoje = new Date()
  const baseResumo = algumFiltroAtivo ? filtrados : lancamentos.filter(l => {
    const venc = l.data_vencimento?.split('T')[0]
    if (!venc) return false
    const d = new Date(venc + 'T00:00:00')
    return d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()
  })
  const valorEfetivo = (l) => parseFloat((l.status === 'pago' && l.valor_pago != null) ? l.valor_pago : l.valor) || 0
  const entradasResumo = baseResumo.filter(l => l.tipo === 'entrada').reduce((s, l) => s + valorEfetivo(l), 0)
  const saidasResumo = baseResumo.filter(l => l.tipo === 'saida').reduce((s, l) => s + valorEfetivo(l), 0)
  const saldo = entradasResumo - saidasResumo

  return (
    <>
      <div className="topbar">
        <h2>Fluxo de Caixa</h2>
        <button className="btn btn-primary" onClick={abrirNovo}>+ Novo Lançamento</button>
      </div>
      <div className="page-content">
        <div className="metrics" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
          <div className="metric-card">
            <div className="metric-label">Entradas {algumFiltroAtivo ? '(filtrado)' : 'do mês'}</div>
            <div className="metric-value val-pos">R$ {entradasResumo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Saídas {algumFiltroAtivo ? '(filtrado)' : 'do mês'}</div>
            <div className="metric-value val-neg">R$ {saidasResumo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Saldo {algumFiltroAtivo ? '(filtrado)' : 'do mês'}</div>
            <div className="metric-value" style={{ color: saldo >= 0 ? '#3B6D11' : '#A32D2D' }}>R$ {saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div className="card" style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: 8, right: 16, fontSize: 12, color: '#888' }}>
            {filtrados.length} {filtrados.length === 1 ? 'registro' : 'registros'}
          </div>
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
                <label className="form-label">Tipo</label>
                <select className="form-select" value={filtro} onChange={e => setFiltro(e.target.value)}>
                  <option value="todos">Todos</option>
                  <option value="entrada">Entradas</option>
                  <option value="saida">Saídas</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
                <label className="form-label">Status</label>
                <select className="form-select" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
                <label className="form-label">Categoria</label>
                <select className="form-select" value={filtroCategoriaId} onChange={e => setFiltroCategoriaId(e.target.value)}>
                  <option value="">Todas</option>
                  {categoriasFiltro.map(c => <option key={c.id} value={c.id}>{c.caminho}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
                <label className="form-label">Origem/Destino</label>
                <select className="form-select" value={filtroOrigemDestinoId} onChange={e => setFiltroOrigemDestinoId(e.target.value)}>
                  <option value="">Todos</option>
                  {origensFiltro.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <fieldset style={{ border: '1px solid #ccc', borderRadius: 6, padding: '4px 12px 10px', margin: 0, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <legend style={{ fontSize: 12, color: '#555', padding: '0 4px' }}>Período de análise</legend>
                <div className="form-group" style={{ marginBottom: 0, minWidth: 200 }}>
                  <label className="form-label">Pela data</label>
                  <select className="form-select" value={filtroPelaData} onChange={e => setFiltroPelaData(e.target.value)}>
                    <option value="">Todos</option>
                    <option value="lancamento">Data de Lançamento</option>
                    <option value="vencimento">Data de Vencimento</option>
                    <option value="quitacao">Data de Quitação</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Início</label>
                  <input className="form-input" type="date" value={filtroDataInicio} disabled={!filtroPelaData} onChange={e => setFiltroDataInicio(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Fim</label>
                  <input className="form-input" type="date" value={filtroDataFim} disabled={!filtroPelaData} onChange={e => setFiltroDataFim(e.target.value)} />
                </div>
              </fieldset>
              <button type="button" className="btn btn-secondary btn-sm" onClick={limparFiltros}>Limpar filtros</button>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunasFinanceiro.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map(l => {
                  const st = statusInfo(l.status)
                  const sinal = l.tipo === 'entrada' ? '+' : '-'
                  const corTipo = l.tipo === 'entrada' ? 'val-pos' : 'val-neg'
                  return (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 500 }}>
                        {l.origem_destino_nome || l.descricao}
                        {etiquetaRecorrencia(l) && <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 10 }}>{etiquetaRecorrencia(l)}</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>{fmtData(l.data_lancamento)}</td>
                      <td style={{ textAlign: 'center' }}><span className="badge badge-gray">{l.categoria_nome || '—'}</span></td>
                      <td style={{ textAlign: 'center' }}>{fmtData(l.data_vencimento)}</td>
                      <td style={{ textAlign: 'center' }} className={corTipo}>{sinal} {fmtValor(l.valor)}</td>
                      <td style={{ textAlign: 'center' }}>{fmtData(l.data_pagamento)}</td>
                      <td style={{ textAlign: 'center' }} className={corTipo}>{l.data_pagamento && l.valor_pago != null ? `${sinal} ${fmtValor(l.valor_pago)}` : '—'}</td>
                      <td title={l.observacoes || ''} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#555' }}>{l.observacoes || '—'}</td>
                      <td style={{ textAlign: 'center' }}><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td style={{ textAlign: 'center' }}><button type="button" className="btn btn-secondary btn-sm btn-icon" title="Editar" onClick={() => abrirEditar(l)}><IconEditar size={14} /></button></td>
                    </tr>
                  )
                })}
                {filtrados.length === 0 && <tr><td colSpan={colunasFinanceiro.length + 1} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>Nenhum lançamento</td></tr>}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="lançamento" />
        </div>
      </div>

      {modal && (
        <LancamentoModal lancamentoId={lancamentoEditId} onClose={fecharModal} onSaved={carregar} />
      )}
    </>
  )
}
